// ---------------------------------------------------------------------------
// Texture Streaming — Distance-priority deferred texture loading.
//
// Instead of loading all PBR textures up front, this system queues texture
// load requests and processes them in priority order (nearest to camera first).
// Materials get a tiny placeholder (64x64 solid color) immediately, then the
// real texture is streamed in as bandwidth allows.
//
// Phase 10 Optimization — reduces initial load time and memory pressure
// ---------------------------------------------------------------------------

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MapType = 'map' | 'normalMap' | 'roughnessMap' | 'aoMap';

interface StreamRequest {
  material: THREE.MeshStandardMaterial;
  mapType: MapType;
  path: string;
  priority: number;
  distance: number;
}

// ---------------------------------------------------------------------------
// TextureStreamer
// ---------------------------------------------------------------------------

export class TextureStreamer {
  private queue: StreamRequest[] = [];
  private loading = new Set<string>();
  private loaded = new Map<string, THREE.Texture>();
  private placeholders = new Map<string, THREE.Texture>();
  private maxConcurrent: number;
  private activeLoads = 0;
  private textureLoader: THREE.TextureLoader;

  constructor(maxConcurrent = 4) {
    this.maxConcurrent = maxConcurrent;
    this.textureLoader = new THREE.TextureLoader();
  }

  /**
   * Create a 64x64 placeholder texture with the given dominant color.
   * Placeholder textures are cached by hex color and reused.
   */
  private createPlaceholder(color: THREE.Color): THREE.Texture {
    const key = color.getHexString();
    if (this.placeholders.has(key)) return this.placeholders.get(key)!;

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = `#${color.getHexString()}`;
    ctx.fillRect(0, 0, 64, 64);

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    this.placeholders.set(key, tex);
    return tex;
  }

  /**
   * Create a flat normal-map placeholder (128, 128, 255 = pointing up).
   */
  private createNormalPlaceholder(): THREE.Texture {
    const key = '__normal_flat';
    if (this.placeholders.has(key)) return this.placeholders.get(key)!;

    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 4;
    const ctx = canvas.getContext('2d')!;
    // Flat normal = (0.5, 0.5, 1.0) in tangent space → rgb(128, 128, 255)
    ctx.fillStyle = 'rgb(128, 128, 255)';
    ctx.fillRect(0, 0, 4, 4);

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.LinearSRGBColorSpace;
    this.placeholders.set(key, tex);
    return tex;
  }

  /**
   * Queue a texture for streaming. If the texture is already loaded or
   * loading, the request is skipped.
   *
   * A placeholder is set immediately on the material for visual consistency.
   */
  enqueue(request: StreamRequest): void {
    const key = `${request.path}_${request.mapType}`;
    if (this.loaded.has(key) || this.loading.has(key)) {
      // If already loaded, apply directly
      if (this.loaded.has(key)) {
        (request.material as any)[request.mapType] = this.loaded.get(key)!;
        request.material.needsUpdate = true;
      }
      return;
    }

    // Set placeholder immediately for visual consistency
    if (request.mapType === 'map' && !request.material.map) {
      request.material.map = this.createPlaceholder(request.material.color);
      request.material.needsUpdate = true;
    } else if (request.mapType === 'normalMap' && !request.material.normalMap) {
      request.material.normalMap = this.createNormalPlaceholder();
      request.material.needsUpdate = true;
    }

    this.queue.push(request);
  }

  /**
   * Process the queue — load textures in distance-priority order.
   * Call this each frame or at a fixed interval (e.g. every 100ms).
   *
   * @param _cameraPosition - Current camera world position (for priority sorting)
   */
  processQueue(_cameraPosition?: THREE.Vector3): void {
    if (this.activeLoads >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    // Sort by distance (nearest first), then by priority
    this.queue.sort((a, b) => {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.priority - b.priority;
    });

    while (this.activeLoads < this.maxConcurrent && this.queue.length > 0) {
      const request = this.queue.shift()!;
      const key = `${request.path}_${request.mapType}`;

      // Check again in case it was loaded while queued
      if (this.loaded.has(key)) {
        (request.material as any)[request.mapType] = this.loaded.get(key)!;
        request.material.needsUpdate = true;
        continue;
      }

      if (this.loading.has(key)) continue;

      this.loading.add(key);
      this.activeLoads++;

      this.textureLoader.load(
        request.path,
        (texture: THREE.Texture) => {
          texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
          texture.anisotropy = 4;

          // Set correct color space
          if (request.mapType === 'map') {
            texture.colorSpace = THREE.SRGBColorSpace;
          } else {
            texture.colorSpace = THREE.LinearSRGBColorSpace;
          }

          this.loaded.set(key, texture);
          this.loading.delete(key);
          this.activeLoads--;

          // Apply to material
          (request.material as any)[request.mapType] = texture;
          request.material.needsUpdate = true;
        },
        undefined,
        (_error: unknown) => {
          // Load failed — keep the placeholder
          this.loading.delete(key);
          this.activeLoads--;
        },
      );
    }
  }

  /**
   * Update distances on queued items based on current camera position
   * and a set of meshes that reference the queued materials.
   */
  updatePriorities(cameraPosition: THREE.Vector3, meshes: THREE.Mesh[]): void {
    // Build a material → distance map
    const materialDistances = new Map<string, number>();
    for (const mesh of meshes) {
      const dist = cameraPosition.distanceTo(mesh.position);
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (mat && mat.uuid) {
        const existing = materialDistances.get(mat.uuid);
        if (existing === undefined || dist < existing) {
          materialDistances.set(mat.uuid, dist);
        }
      }
    }

    // Update queued request distances
    for (const req of this.queue) {
      const dist = materialDistances.get(req.material.uuid);
      if (dist !== undefined) {
        req.distance = dist;
      }
    }
  }

  /** Get loading statistics. */
  getStats(): { loaded: number; queued: number; loading: number } {
    return {
      loaded: this.loaded.size,
      queued: this.queue.length,
      loading: this.activeLoads,
    };
  }

  /** Check if all queued textures have finished loading. */
  isComplete(): boolean {
    return this.queue.length === 0 && this.activeLoads === 0;
  }

  /** Dispose all loaded and placeholder textures. */
  dispose(): void {
    this.loaded.forEach((t) => t.dispose());
    // L09: Null out canvas references on placeholder textures to prevent canvas element leaks
    this.placeholders.forEach((t) => {
      t.dispose();
      t.image = null;
    });
    this.loaded.clear();
    this.placeholders.clear();
    this.queue = [];
    this.loading.clear();
    this.activeLoads = 0;
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let streamer: TextureStreamer | null = null;

/** Get or create the global TextureStreamer instance. */
export function getTextureStreamer(maxConcurrent?: number): TextureStreamer {
  if (!streamer) {
    streamer = new TextureStreamer(maxConcurrent);
  }
  return streamer;
}

/** Dispose the global TextureStreamer. */
export function disposeTextureStreamer(): void {
  streamer?.dispose();
  streamer = null;
}
